/**
 * How a state looks and what it is called.
 *
 * One table, consulted by the pills, the bar strip, the legend, the incident
 * timeline and the developer area. The reason it is one table rather than a
 * switch in each of them is the rule the palette depends on: **a status colour
 * never carries meaning alone.** Every entry below pairs a colour with an icon
 * *and* a word, so there is no way to render a state and accidentally ship only
 * the colour — the shape of the data makes the accessible version the easy one.
 */

import type { MonitorState, Overall } from '@shared/status';

export interface Tone {
  /** Large areas: bars, banner washes. */
  fill: string;
  /** Text and 2px strokes. A different step from `fill` — see styles.css. */
  ink: string;
  /** Tinted backgrounds behind ink. */
  soft: string;
  /** Lucide name. Never optional. */
  icon: string;
  label: string;
}

export const STATE_TONE: Record<MonitorState, Tone> = {
  up: {
    fill: 'var(--st-up-fill)',
    ink: 'var(--st-up-ink)',
    soft: 'var(--st-up-soft)',
    icon: 'check-circle-2',
    label: 'Operational'
  },
  down: {
    fill: 'var(--st-down-fill)',
    ink: 'var(--st-down-ink)',
    soft: 'var(--st-down-soft)',
    icon: 'x-circle',
    label: 'Down'
  },
  maintenance: {
    fill: 'var(--st-maint-fill)',
    ink: 'var(--st-maint-ink)',
    soft: 'var(--st-maint-soft)',
    icon: 'wrench',
    label: 'Maintenance'
  },
  paused: {
    fill: 'var(--st-none-fill)',
    ink: 'var(--st-none-ink)',
    soft: 'var(--st-none-soft)',
    icon: 'pause',
    label: 'Paused'
  },
  unknown: {
    fill: 'var(--st-none-fill)',
    ink: 'var(--st-none-ink)',
    soft: 'var(--st-none-soft)',
    icon: 'circle',
    label: 'No data'
  }
};

export const OVERALL_TONE: Record<Overall, Tone> = {
  operational: { ...STATE_TONE.up, label: 'All Systems Operational' },
  maintenance: { ...STATE_TONE.maintenance, label: 'Under Maintenance' },
  degraded: {
    fill: 'var(--st-degraded-fill)',
    ink: 'var(--st-degraded-ink)',
    soft: 'var(--st-degraded-soft)',
    icon: 'triangle-alert',
    label: 'Degraded Performance'
  },
  partial_outage: {
    fill: 'var(--st-degraded-fill)',
    ink: 'var(--st-degraded-ink)',
    soft: 'var(--st-degraded-soft)',
    icon: 'triangle-alert',
    label: 'Partial System Outage'
  },
  major_outage: { ...STATE_TONE.down, label: 'Major System Outage' }
};

/** Impact, for an incident header. Maintenance is never an "impact" — it has
 *  its own kind — so `none` is what a purely informational notice carries. */
export const IMPACT_TONE: Record<string, Tone> = {
  none: {
    fill: 'var(--st-maint-fill)',
    ink: 'var(--st-maint-ink)',
    soft: 'var(--st-maint-soft)',
    icon: 'info',
    label: 'Informational'
  },
  minor: {
    fill: 'var(--st-degraded-fill)',
    ink: 'var(--st-degraded-ink)',
    soft: 'var(--st-degraded-soft)',
    icon: 'triangle-alert',
    label: 'Minor'
  },
  major: { ...STATE_TONE.down, label: 'Major' },
  critical: { ...STATE_TONE.down, label: 'Critical' }
};

/** Mirrors worker/lib/format.ts. The two must agree — an email and the page it
 *  links to calling the same state different things is its own small outage. */
export function stateLabel(state: string): string {
  switch (state) {
    case 'investigating':
      return 'Investigating';
    case 'identified':
      return 'Identified';
    case 'monitoring':
      return 'Monitoring';
    case 'resolved':
      return 'Resolved';
    case 'scheduled':
      return 'Scheduled';
    case 'in_progress':
      return 'In progress';
    case 'completed':
      return 'Completed';
    default:
      return state;
  }
}

export function incidentStateTone(state: string): Tone {
  switch (state) {
    case 'resolved':
    case 'completed':
      return STATE_TONE.up;
    case 'scheduled':
    case 'in_progress':
      return STATE_TONE.maintenance;
    case 'monitoring':
      return OVERALL_TONE.degraded;
    default:
      return STATE_TONE.down;
  }
}

// ── formatting ──────────────────────────────────────────────────────────────

/** "2 minutes", "6 hours", "3 days". Used for "down for …" and "in …". */
export function humanDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${Math.round(h / 24)} days`;
}

/**
 * Timestamps render in the reader's own zone, deliberately unlike email.
 *
 * A person looking at the page is asking "is it broken *now*", and answering in
 * UTC makes them do arithmetic to find out. Email is the opposite case — it is
 * read later, forwarded, and quoted — so worker/lib/format.ts spells out UTC
 * there. The divergence is intentional rather than an inconsistency.
 */
export function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/** "12 August 2026" from a 'YYYY-MM-DD' rollup key. Parsed as UTC — the key is
 *  a UTC day, and letting the browser read it as local would shift every
 *  tooltip by a day for anyone west of Greenwich. */
export function formatDayKey(day: string): string {
  const [y, m, d] = day.split('-').map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return day;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

export function formatRelative(unixSeconds: number): string {
  const diff = Date.now() / 1000 - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  return formatDate(unixSeconds);
}
